// src/App.js

import React, { Suspense, createContext, lazy, useState, useContext, useEffect } from 'react';
import './App.css';
import { CartProvider } from './context/CartContext';
import BucksHeader from './components/BucksHeader';
import BucksLanding from './components/BucksLanding';
import BucksFooter from './components/BucksFooter';
import { installApiAuthInterceptor } from './utils/apiAuth';
import {
  ADMIN_IDLE_TIMEOUT_MS,
  ADMIN_IDLE_WARNING_MS,
  REMEMBERED_SESSION_DURATION_MS,
  canUseAdminPortal,
  canUsePartnerPortal,
  canUseWorkerPortal,
  clearAuthSessionStorage,
  getPreferredPortal,
  getSessionPolicyForUser,
  getSessionPolicyForUserRaw
} from './utils/authSession';

const BucksShop = lazy(() => import('./components/BucksShop'));
const BucksAbout = lazy(() => import('./components/BucksAbout'));
const BucksCommunity = lazy(() => import('./components/BucksCommunity'));
const BucksContact = lazy(() => import('./components/BucksContact'));
const BucksFarmstandFAQ = lazy(() => import('./components/BucksFarmstandFAQ'));
const BucksBox = lazy(() => import('./components/BucksBox'));
const BucksRecipes = lazy(() => import('./components/BucksRecipes'));
const BucksBasket = lazy(() => import('./components/BucksBasket'));
const BucksCheckout = lazy(() => import('./components/BucksCheckout'));
const BucksAdminLogin = lazy(() => import('./components/BucksAdminLogin'));
const BucksPasswordReset = lazy(() => import('./components/BucksPasswordReset'));
const BucksAdminDashboard = lazy(() => import('./components/BucksAdminDashboard'));
const BucksWorkerDashboard = lazy(() => import('./components/BucksWorkerDashboard'));
const BucksCommunityHarvestDashboard = lazy(() => import('./components/BucksCommunityHarvestDashboard'));
const BucksAdminCommunityHarvest = lazy(() => import('./components/BucksAdminCommunityHarvest'));
const IdleTimerProvider = lazy(() => import('./components/IdleTimerProvider'));
const BucksPrivacyPolicy = lazy(() => import('./components/BucksPrivacyPolicy'));
const BucksTermsOfService = lazy(() => import('./components/BucksTermsOfService'));
const BucksNewsletterUnsubscribe = lazy(() => import('./components/BucksNewsletterUnsubscribe'));
const BucksNewsletterConfirm = lazy(() => import('./components/BucksNewsletterConfirm'));
const BucksNewsletterInvitation = lazy(() => import('./components/BucksNewsletterInvitation'));

// Create a Search Context to share search data across components if needed
export const SearchContext = createContext();
export const useSearch = () => {
  const context = useContext(SearchContext);
  if (context === undefined) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};

// Create Auth Context for admin authentication
export const AuthContext = createContext();
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const ADMIN_LOGIN_ROUTE      = '/admin/login';
const ADMIN_DASHBOARD_ROUTE  = '/admin/dashboard';
const WORKER_LOGIN_ROUTE     = '/workers/login';
const WORKER_DASHBOARD_ROUTE = '/worker/dashboard';
const PARTNER_LOGIN_ROUTE = '/community-harvest/login';
const PARTNER_DASHBOARD_ROUTE = '/community-harvest/dashboard';

installApiAuthInterceptor();

const readAuthFromStorage = (storage) => {
  const token = storage.getItem('adminToken');
  if (!token) return null;

  return {
    token,
    userRaw: storage.getItem('adminUser') || '{}',
    lastActivityTime: parseInt(storage.getItem('lastActivity') || '0', 10),
    expiresAt: storage.getItem('expiresAt') || '',
    rememberMe: storage.getItem('adminRememberMe') === 'true',
    rememberedUntil: storage.getItem('rememberedUntil') || ''
  };
};

const writeAuthToStorage = (storage, authData) => {
  if (!authData.token) {
    throw new Error('A valid session token is required.');
  }
  storage.setItem('adminToken', authData.token);
  storage.setItem('adminUser', JSON.stringify(authData.userData || {}));
  storage.setItem('lastActivity', authData.lastActivity || Date.now().toString());
  storage.setItem('adminRememberMe', authData.rememberMe ? 'true' : 'false');

  if (authData.expiresAt) {
    storage.setItem('expiresAt', authData.expiresAt);
  } else {
    storage.removeItem('expiresAt');
  }

  if (authData.rememberedUntil) {
    storage.setItem('rememberedUntil', authData.rememberedUntil);
  } else {
    storage.removeItem('rememberedUntil');
  }
};

const mirrorAuthRecordToSession = (authRecord) => {
  sessionStorage.setItem('adminToken', authRecord.token);
  sessionStorage.setItem('adminUser', authRecord.userRaw);
  sessionStorage.setItem('lastActivity', authRecord.lastActivityTime ? authRecord.lastActivityTime.toString() : Date.now().toString());
  sessionStorage.setItem('adminRememberMe', authRecord.rememberMe ? 'true' : 'false');

  if (authRecord.expiresAt) {
    sessionStorage.setItem('expiresAt', authRecord.expiresAt);
  } else {
    sessionStorage.removeItem('expiresAt');
  }

  if (authRecord.rememberedUntil) {
    sessionStorage.setItem('rememberedUntil', authRecord.rememberedUntil);
  } else {
    sessionStorage.removeItem('rememberedUntil');
  }
};

const isPastDateTime = (value) => Boolean(value && new Date(value) < new Date());

const evaluateAuthRecord = (authRecord, source) => {
  if (!authRecord?.token) {
    return { valid: false, remembered: false };
  }

  const remembered = authRecord.rememberMe || source === 'local';
  const { idleTimeoutMs } = getSessionPolicyForUserRaw(authRecord.userRaw);
  const inactive = !remembered
    && authRecord.lastActivityTime
    && Date.now() - authRecord.lastActivityTime > idleTimeoutMs;
  const tokenExpired = isPastDateTime(authRecord.expiresAt);
  const rememberedExpired = remembered && isPastDateTime(authRecord.rememberedUntil);

  return {
    valid: !inactive && !tokenExpired && !rememberedExpired,
    remembered
  };
};

function App() {
  // Simple routing based on pathname
  const pathname     = window.location.pathname;
  const search       = window.location.search;
  const searchParams = new URLSearchParams(search);

  // Dynamically set document.title based on current route
  useEffect(() => {
    const pageSearchParams = new URLSearchParams(search);

    const titles = {
      '/':                  'Home | Bucks Farmstand',
      '/shop':              'Shop | Bucks Farmstand',
      '/about':             'About | Bucks Farmstand',
      '/community':         'Farmers Markets & Community Partners | Bucks Farmstand',
      '/contact':           'Contact | Bucks Farmstand',
      '/faq':               'FAQ | Bucks Farmstand',
      '/bucks-box':         'Bucks Box | Bucks Farmstand',
      '/recipes':           'Recipes | Bucks Farmstand',
      '/basket':            'Your Basket | Bucks Farmstand',
      '/checkout':          'Checkout | Bucks Farmstand',
      '/privacy':           'Privacy Policy | Bucks Farmstand',
      '/terms':             'Terms of Service | Bucks Farmstand',
      '/unsubscribe':       'Unsubscribe | Bucks Farmstand',
      '/newsletter/confirm': 'Confirm Newsletter | Bucks Farmstand',
      '/newsletter/invitation': 'Newsletter Preference | Bucks Farmstand',
      '/admin/login':       'Admin Login | Bucks Farmstand',
      '/admin/forgot-password': 'Reset Admin Password | Bucks Farmstand',
      '/admin/reset-password': 'Set Admin Password | Bucks Farmstand',
      '/admin':             'Admin Dashboard | Bucks Farmstand',
      '/admin/dashboard':   'Admin Dashboard | Bucks Farmstand',
      '/workers/login':     'Worker Login | Bucks Farmstand',
      '/workers/forgot-password': 'Reset Worker Password | Bucks Farmstand',
      '/workers/reset-password': 'Set Worker Password | Bucks Farmstand',
      '/worker/dashboard':  'Worker Dashboard | Bucks Farmstand',
      '/community-harvest': 'Community Harvest Sign In | Bucks Farmstand',
      '/community-harvest/login': 'Community Harvest Sign In | Bucks Farmstand',
      '/community-harvest/dashboard': 'Community Harvest | Bucks Farmstand',
      '/community-harvest/admin-preview': 'Community Harvest Staff Preview | Bucks Farmstand',
      '/community-harvest/forgot-password': 'Community Harvest Password Reset | Bucks Farmstand',
      '/community-harvest/reset-password': 'Community Harvest Password Reset | Bucks Farmstand'
    };

    let title = titles[pathname] || 'Bucks Farmstand';

    // Order confirmation page with order number
    if (pathname === '/order-confirmation') {
      const orderNum = pageSearchParams.get('order');
      title = orderNum
        ? `Order Confirmation #${orderNum} | Bucks Farmstand`
        : 'Order Confirmation | Bucks Farmstand';
    }

    // Basket with checkout query param
    if (pathname === '/basket' && pageSearchParams.get('checkout') === 'true') {
      title = 'Checkout | Bucks Farmstand';
    }

    document.title = title;

    const defaultDescription = 'Buck\'s Farmstand in Alsip, Illinois offers seasonal produce, pasture-raised eggs, artisan breads, and local favorites with delivery and farmstand pickup options.';
    const defaultKeywords = 'Buck\'s Farmstand, Alsip farm stand, fresh produce Alsip, local eggs, artisan breads, apple sauce, sustainable farming, community market';
    const routeDescriptions = {
      '/bucks-box': 'Learn about Buck\'s Box seasonal produce subscriptions, delivery timing, waitlist signup, and local delivery options from Buck\'s Farmstand in Alsip, Illinois.',
      '/community': 'Find Buck\'s Farmstand at 2026 farmers markets in Tinley Park, Hickory Hills, Chicago Ridge, and Wilmington, plus food pantry and local food partnerships.',
      '/contact': 'Contact Buck\'s Farmstand in Alsip, Illinois for product questions, special orders, newsletter signup, delivery help, and free or extended local delivery details.',
      '/faq': 'Read Buck\'s Farmstand FAQs covering free delivery within 7.5 miles, extended delivery up to 15 miles, minimum orders, seasonality, special orders, and newsletter signup details.',
      '/privacy': 'Read Buck\'s Farmstand Privacy Policy to understand what data we collect, how we use it, and the choices available to you.',
      '/terms': 'Review Buck\'s Farmstand Terms of Service, including delivery details, payment terms, return policy, and customer responsibilities.'
    };
    const routeKeywords = {
      '/bucks-box': 'Buck\'s Box subscription, Buck\'s Farmstand delivery, Alsip produce box, local produce subscription, seasonal produce delivery',
      '/community': 'Buck\'s Farmstand farmers markets, Hickory Hills farmers market, Hills Farmers Market and More, Tinley Park Farmers Market, Chicago Ridge Farmers Market, Wilmington Farmers Market, Alsip farmstand, southwest Chicago produce, food pantry support, Foodshed Fresh Thyme partnership',
      '/contact': 'contact Buck\'s Farmstand, Alsip farmstand phone, Buck\'s Farmstand delivery help, special orders Alsip, local produce delivery',
      '/faq': 'Buck\'s Farmstand FAQ, Buck\'s Farmstand delivery radius, free delivery Alsip, extended produce delivery, farmstand pickup, delivery minimum'
    };

    const description = routeDescriptions[pathname] || defaultDescription;
    const keywords = routeKeywords[pathname] || defaultKeywords;
    const canonicalPath = pathname === '/' ? '/' : pathname;
    const canonicalUrl = `https://www.bucksfarmstand.com${canonicalPath}`;

    const upsertMetaTag = (attributeName, attributeValue, content) => {
      let tag = document.head.querySelector(`meta[${attributeName}="${attributeValue}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attributeName, attributeValue);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };

    upsertMetaTag('name', 'description', description);
    upsertMetaTag('name', 'keywords', keywords);
    upsertMetaTag('property', 'og:title', title);
    upsertMetaTag('property', 'og:description', description);
    upsertMetaTag('property', 'og:url', canonicalUrl);
    upsertMetaTag('name', 'twitter:title', title);
    upsertMetaTag('name', 'twitter:description', description);
    if (pathname.startsWith('/community-harvest')) {
      upsertMetaTag('name', 'robots', 'noindex, nofollow');
    }

    let canonicalLink = document.head.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonicalUrl);
  }, [pathname, search]);

  // iOS Safari can restore stale scroll positions from page cache.
  // Keep public pages pinned to top on fresh/returned navigations.
  useEffect(() => {
    if (pathname.startsWith('/admin') || pathname.startsWith('/worker') || pathname.startsWith('/community-harvest') || window.location.hash) {
      return;
    }

    window.scrollTo(0, 0);

    const handlePageShow = (event) => {
      if (event.persisted && !window.location.hash) {
        window.scrollTo(0, 0);
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [pathname]);

  // Search context state
  const [searchQuery, setSearchQuery]     = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching]     = useState(false);

  // Auth context state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminUser, setAdminUser]             = useState(null);
  const [authLoading, setAuthLoading]         = useState(true);
  const [isRememberedSession, setIsRememberedSession] = useState(false);

  // Track the last activity timestamp
  const [lastActivity, setLastActivity] = useState(Date.now());

  // Helper function to determine user dashboard based on role
  const getUserDashboardRoute = (user) => {
    if (!user || !user.role) return ADMIN_LOGIN_ROUTE;
    const portal = getPreferredPortal(user);
    if (portal === 'worker') return WORKER_DASHBOARD_ROUTE;
    if (portal === 'partner') return PARTNER_DASHBOARD_ROUTE;
    return ADMIN_DASHBOARD_ROUTE;
  };

  // Helper function to get the appropriate dashboard component
  const getDashboardComponent = (user, portal = getPreferredPortal(user)) => {
    if (!user || !user.role) return null;
    if (portal === 'partner' && canUsePartnerPortal(user)) {
      return (
        <IdleTimerProvider timeout={ADMIN_IDLE_TIMEOUT_MS} warningTime={ADMIN_IDLE_WARNING_MS}>
          <BucksCommunityHarvestDashboard />
        </IdleTimerProvider>
      );
    }
    if (portal === 'worker' && canUseWorkerPortal(user)) {
      const { idleTimeoutMs, warningTimeMs } = getSessionPolicyForUser(user);
      return (
        <IdleTimerProvider timeout={idleTimeoutMs} warningTime={warningTimeMs}>
          <BucksWorkerDashboard />
        </IdleTimerProvider>
      );
    }
    return (
      <IdleTimerProvider timeout={ADMIN_IDLE_TIMEOUT_MS} warningTime={ADMIN_IDLE_WARNING_MS}>
        <BucksAdminDashboard />
      </IdleTimerProvider>
    );
  };

  // Check for existing authentication on mount and periodically
  useEffect(() => {
    const checkAuth = () => {
      const sessionAuth = readAuthFromStorage(sessionStorage);
      const localAuth = readAuthFromStorage(localStorage);
      const sessionStatus = evaluateAuthRecord(sessionAuth, 'session');
      const localStatus = evaluateAuthRecord(localAuth, 'local');
      const authRecord = sessionStatus.valid
        ? sessionAuth
        : localStatus.valid
          ? localAuth
          : null;
      const authStatus = sessionStatus.valid ? sessionStatus : localStatus;

      if (authRecord && authStatus.valid) {
        setIsAuthenticated(true);
        setIsRememberedSession(authStatus.remembered);
        try {
          const userData = JSON.parse(authRecord.userRaw || '{}');
          setAdminUser(userData);
          mirrorAuthRecordToSession(authRecord);
          
          // Auto-redirect only when the active session belongs to the portal
          // being opened. This keeps worker invite links useful for managers
          // testing the email while an admin session is still active.
          const isWorkerLoginForWorker = pathname === WORKER_LOGIN_ROUTE && canUseWorkerPortal(userData);
          const isAdminLoginForAdmin = pathname === ADMIN_LOGIN_ROUTE && canUseAdminPortal(userData);
          const isPartnerLoginForPartner = pathname === PARTNER_LOGIN_ROUTE && canUsePartnerPortal(userData);
          if (isAdminLoginForAdmin || isWorkerLoginForWorker || isPartnerLoginForPartner) {
            const dashboardRoute = isWorkerLoginForWorker
              ? WORKER_DASHBOARD_ROUTE
              : isPartnerLoginForPartner ? PARTNER_DASHBOARD_ROUTE : ADMIN_DASHBOARD_ROUTE;
            window.location.href = dashboardRoute;
            return;
          }
        } catch (error) {
          console.error('Error parsing admin user data:', error);
          logout();
        }
      } else {
        // Clear all auth data on expiration or missing token
        clearAuthSessionStorage(sessionStorage);
        if (!localStatus.valid) {
          clearAuthSessionStorage(localStorage);
        }
        setIsAuthenticated(false);
        setIsRememberedSession(false);
      }

      setAuthLoading(false);
    };

    // Initial check
    checkAuth();
    // Periodic check every minute
    const authCheckInterval = setInterval(checkAuth, 60 * 1000);
    return () => clearInterval(authCheckInterval);
  }, [pathname]);

  // Login function for auth context
  const touchAuthActivity = () => {
    const now = Date.now().toString();
    sessionStorage.setItem('lastActivity', now);
    if (localStorage.getItem('adminRememberMe') === 'true') {
      localStorage.setItem('lastActivity', now);
    }
    // Activity fires frequently; keep this storage-only so dashboards do not refetch while users move around.
  };

  const login = (userData, token, expiresAt, options = {}) => {
    if (!token) {
      throw new Error('Login response did not include a session token.');
    }

    const rememberMe = Boolean(options.rememberMe);
    const lastActivityValue = Date.now().toString();
    const rememberedUntil = rememberMe
      ? new Date(Date.now() + REMEMBERED_SESSION_DURATION_MS).toISOString()
      : '';

    setIsAuthenticated(true);
    setAdminUser(userData);
    setIsRememberedSession(rememberMe);

    clearAuthSessionStorage(sessionStorage);
    clearAuthSessionStorage(localStorage);

    const authData = {
      userData,
      token,
      expiresAt,
      rememberMe,
      rememberedUntil,
      lastActivity: lastActivityValue
    };

    writeAuthToStorage(sessionStorage, authData);
    if (rememberMe) {
      writeAuthToStorage(localStorage, authData);
    }
    setLastActivity(Date.now());
    
    // Redirect to appropriate dashboard based on role
    const dashboardRoute = getUserDashboardRoute(userData);
    window.location.href = dashboardRoute;
  };

  // Logout function for auth context
  const logout = () => {
    const userRole = adminUser?.role?.toLowerCase();
    const logoutRoute = userRole === 'community_partner' || pathname.startsWith('/community-harvest')
      ? PARTNER_LOGIN_ROUTE
      : userRole === 'worker' || pathname.startsWith('/worker') || pathname.startsWith('/workers')
        ? WORKER_LOGIN_ROUTE
        : ADMIN_LOGIN_ROUTE;

    setIsAuthenticated(false);
    setAdminUser(null);
    setIsRememberedSession(false);

    clearAuthSessionStorage(sessionStorage);
    clearAuthSessionStorage(localStorage);

    window.location.href = logoutRoute;
  };

  const currentAuthRecord = readAuthFromStorage(sessionStorage) || readAuthFromStorage(localStorage);

  // Auth context value, now including expiresAt
  const authContextValue = {
    isAuthenticated,
    adminUser,
    login,
    logout,
    expiresAt:   currentAuthRecord?.expiresAt || null,
    isRemembered: isRememberedSession,
    lastActivity,
    setLastActivity,
    touchAuthActivity
  };

  // Render component based on route, wrapping admin routes with IdleTimerProvider
  const renderPage = () => {
    if (authLoading && (pathname.startsWith('/admin') || pathname.startsWith('/worker') || pathname.startsWith('/workers') || pathname.startsWith('/community-harvest'))) {
      return <div className="admin-loading">Loading...</div>;
    }

    switch (pathname) {
      case '/shop':
        return <BucksShop />;
      case '/about':
        return <BucksAbout />;
      case '/community':
        return <BucksCommunity />;
      case '/contact':
        return <BucksContact />;
      case '/faq':
        return <BucksFarmstandFAQ />;
      case '/bucks-box':
        return <BucksBox />;
      case '/recipes':
        return <BucksRecipes />;
      case '/privacy':
        return <BucksPrivacyPolicy />;
      case '/terms':
        return <BucksTermsOfService />;
      case '/unsubscribe':
        return <BucksNewsletterUnsubscribe />;
      case '/newsletter/confirm':
        return <BucksNewsletterConfirm />;
      case '/newsletter/invitation':
        return <BucksNewsletterInvitation />;
      case '/basket':
        if (searchParams.get('checkout') === 'true') {
          return <BucksCheckout />;
        }
        return <BucksBasket />;
      case '/checkout':
        return <BucksCheckout />;
      case '/order-confirmation':
        return (
          <BucksCheckout
            initialOrderComplete={true}
            orderNumber={searchParams.get('order')}
          />
        );
      case '/admin/login':
        return isAuthenticated && canUseAdminPortal(adminUser) ? (
          // If already authenticated, redirect to appropriate dashboard
          getDashboardComponent(adminUser, 'admin')
        ) : (
          <BucksAdminLogin />
        );
      case '/admin/forgot-password':
        return <BucksPasswordReset portal="admin" mode="request" />;
      case '/admin/reset-password':
        return <BucksPasswordReset portal="admin" mode="reset" />;
      case '/workers/login':
        return isAuthenticated && canUseWorkerPortal(adminUser) ? (
          getDashboardComponent(adminUser, 'worker')
        ) : (
          <BucksAdminLogin portal="worker" />
        );
      case '/workers/forgot-password':
        return <BucksPasswordReset portal="worker" mode="request" />;
      case '/workers/reset-password':
        return <BucksPasswordReset portal="worker" mode="reset" />;
      case '/community-harvest':
      case '/community-harvest/login':
        return isAuthenticated && canUsePartnerPortal(adminUser)
          ? getDashboardComponent(adminUser, 'partner')
          : <BucksAdminLogin portal="partner" />;
      case '/community-harvest/forgot-password':
        return <BucksPasswordReset portal="partner" mode="request" />;
      case '/community-harvest/reset-password':
        return <BucksPasswordReset portal="partner" mode="reset" />;
      case '/community-harvest/dashboard':
        if (process.env.NODE_ENV === 'development' && searchParams.get('preview') === '1') {
          return <BucksCommunityHarvestDashboard preview />;
        }
        if (!isAuthenticated || !canUsePartnerPortal(adminUser)) {
          return <BucksAdminLogin portal="partner" />;
        }
        return getDashboardComponent(adminUser, 'partner');
      case '/community-harvest/admin-preview':
        return process.env.NODE_ENV === 'development'
          ? <div className="admin-harvest-preview-page"><a className="admin-harvest-preview-back" href="/community-harvest/login">← Back to Community Harvest</a><h1>Staff workspace preview</h1><BucksAdminCommunityHarvest preview /></div>
          : <BucksAdminLogin portal="partner" />;
      case '/admin':
      case '/admin/dashboard':
        if (!isAuthenticated) {
          return <BucksAdminLogin />;
        }
        // Check if user should be on worker dashboard instead
        if (!canUseAdminPortal(adminUser)) {
          window.location.href = canUsePartnerPortal(adminUser) ? PARTNER_DASHBOARD_ROUTE : WORKER_DASHBOARD_ROUTE;
          return <div className="admin-loading">Redirecting...</div>;
        }
        return (
          <IdleTimerProvider timeout={ADMIN_IDLE_TIMEOUT_MS} warningTime={ADMIN_IDLE_WARNING_MS}>
            <BucksAdminDashboard />
          </IdleTimerProvider>
        );
      case '/worker/dashboard':
        if (!isAuthenticated) {
          return <BucksAdminLogin portal="worker" />;
        }
        // Check if user should be on admin dashboard instead
        if (!canUseWorkerPortal(adminUser)) {
          window.location.href = canUsePartnerPortal(adminUser) ? PARTNER_DASHBOARD_ROUTE : ADMIN_DASHBOARD_ROUTE;
          return <div className="admin-loading">Redirecting...</div>;
        }
        return getDashboardComponent(adminUser, 'worker');
      default:
        return <BucksLanding />;
    }
  };

  // Search context value
  const searchContextValue = {
    searchQuery,
    setSearchQuery,
    searchResults,
    setSearchResults,
    isSearching,
    setIsSearching
  };

  // Determine if we're on an admin/worker route
  const isAdminRoute = pathname.startsWith('/admin') || pathname.startsWith('/worker') || pathname.startsWith('/workers') || pathname.startsWith('/community-harvest');

  return (
    <div className="App">
      <CartProvider>
        <SearchContext.Provider value={searchContextValue}>
          <AuthContext.Provider value={authContextValue}>
            {!isAdminRoute && <BucksHeader />}
            <main className="app-main">
              <Suspense fallback={<div className="page-loading">Loading...</div>}>
                {renderPage()}
              </Suspense>
            </main>
            {!isAdminRoute && <BucksFooter />}
          </AuthContext.Provider>
        </SearchContext.Provider>
      </CartProvider>
    </div>
  );
}

export default App;
